// Pure data-mapping helpers shared between the sales-room screens: hydrate the
// editor `form` from an API row, and build the CRUD payload back from it. The
// visual editor lives in salesRoomCanvas.js; this module carries no UI.

import moment from "moment";

// Action-plan step states. "pending" and "done" keep their original stored
// spellings (they read as "Not started" and "Completed"), so existing plans
// need no migration.
export const MILESTONE_STATUSES = ["pending", "in_progress", "done"];

// Heading above the welcome copy on the buyer dashboard when the seller
// has not set one of their own.
export const DEFAULT_DASHBOARD_TITLE = "Welcome to the sales room";

/**
 * Hydrates form-friendly sections from the API row: sections carry
 * {target_type, target_id} refs; field_references carries the resolved
 * titles/thumbnails. Stale refs (deleted content) are dropped.
 */
export const sectionsFromRoom = (room) => {
  const resolved = room.field_references || [];
  const byKey = {};
  resolved.forEach((ref) => {
    byKey[`${ref.type}:${ref.id}`] = ref;
  });

  return (room.field_sections_data || []).map((section) => ({
    id: section.id,
    title: section.title || "",
    description: section.description || "",
    items: (section.items || [])
      .map((item) => byKey[`${item.target_type}:${item.target_id}`])
      .filter(Boolean),
  }));
};

/**
 * Hydrates form-friendly milestones from the API row (unix due → date input).
 */
export const milestonesFromRoom = (room) => {
  return (room.field_milestones || []).map((milestone) => ({
    ...milestone,
    dueInput: milestone.due
      ? moment.unix(milestone.due).format("YYYY-MM-DD")
      : "",
  }));
};

/**
 * Builds the CRUD payload from form state. Emits both the sections layout
 * (JSON structure) and the deduped flat field_references index the backend
 * keeps for referential integrity.
 */
export const buildRoomPayload = (form) => {
  const references = [];
  const seen = {};

  const sections = form.sections.map((section) => ({
    id: section.id,
    title: section.title,
    description: section.description,
    items: section.items.map((item) => {
      const type = item.type || item.bundle;
      const key = `${type}:${item.id}`;
      if (!seen[key]) {
        seen[key] = true;
        references.push({ bundle: type, id: item.id });
      }
      return { target_type: type, target_id: item.id };
    }),
  }));

  const payload = {
    title: form.title,
    summary: form.description,
    field_company: form.company,
    room_status: form.status,
    field_expiry: form.expiry
      ? Math.floor(new Date(form.expiry).getTime() / 1000)
      : 0,
    field_branding_colour: form.colour,
    field_branding_logo: form.logoFid ? { target_id: form.logoFid } : null,
    field_company_logo: form.companyLogoFid
      ? { target_id: form.companyLogoFid }
      : null,
    field_contacts: form.contacts.filter(
      (contact) => contact.name || contact.email
    ),
    field_sections_data: sections,
    field_references: references,
    field_intro_video: form.introVideoMid || null,
    field_intro_text: form.introText || null,
    field_dashboard_title: form.dashboardTitle || null,
    field_alert_mode: form.alertMode,
    field_daily_digest: !!form.dailyDigest,
    field_access_mode: form.accessMode || "link",
    is_template: !!form.isTemplate,
    // Provenance (created_by/author/created) is set by the buyer portal when
    // someone adds a step there, and passed straight back through so saving
    // from this screen never rewrites a buyer's step as one of ours.
    field_milestones: form.milestones
      .filter((milestone) => milestone.title)
      .map((milestone) => ({
        id: milestone.id,
        title: milestone.title,
        description: milestone.description || "",
        due: milestone.dueInput
          ? Math.floor(new Date(milestone.dueInput).getTime() / 1000)
          : 0,
        assignee: milestone.assignee === "buyer" ? "buyer" : "seller",
        status: MILESTONE_STATUSES.includes(milestone.status)
          ? milestone.status
          : "pending",
        created_by: milestone.created_by === "buyer" ? "buyer" : "seller",
        author: milestone.author || "",
        author_email: milestone.author_email || "",
        created: milestone.created || 0,
      })),
  };

  // Only send the password when the seller actually changed it - the
  // backend treats absence as "keep", empty string as "remove".
  if (form.passwordDirty) {
    payload.field_password = form.password || "";
  }

  return payload;
};
